import { Injectable } from '@angular/core';
import {
  chain,
  isEmpty,
  isNil,
  isNumber,
  reduce,
  some,
  concat,
  isFunction,
  includes,
  filter,
  isEqual,
  set,
  partition,
  head,
  last,
} from 'lodash';
import { Subject, Subscription, Observable, BehaviorSubject } from 'rxjs';
import { CellClassParams, ValueFormatterParams } from 'ag-grid-community';

import {
  getFormControl,
  getFormFieldValue,
} from '@app/components/form/services/schema-form/schema-form-functions';
import { rx, observableCombineLatest } from '@app/shared/rx';
import {
  FieldList,
  IFormRules,
  IGridRowOrCellClassRule,
  IGridFlatInventoryItemVenue,
  IGridFlatInventoryItemVenueTotal,
  IGridInventoryItemVenueTotalFormData,
  ITransferFormFieldPath,
  SORT_ORDERS,
  WIDGET_COMPONENT,
  XUX_GRID_ACTIONS,
  XuxGridAction,
  IXVIListColumn,
  IGridInventoryItemVenueTotalFormCallBack,
  IBaseFormService,
  ENTITY_TYPES,
  ITransferData,
  CELL_RENDERER,
  TOTALS_CATEGORY,
} from '@app/shared/types';

import { XuxEditGridComponent } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.component';
import { alphanumericComparator, DecimalNumber } from '@app/shared/common';

import { NumberFormatService } from '../number-format';
import { calculateUnitCost, getIsShowReceivedColStatus } from './transfer.util';
import { COLUMN_ALIGN_RIGHT } from '@shared/constants/column-align-definition';
import {
  DEPARTMENT_I18N_CODE_MAP,
  TRANSFER_FINAL_STATUS,
} from '@shared/services/transfer/transfer.definition';
import { flatTreeDataByHasChildren } from '@shared/utils';
import { I18n } from '@app/i18n/strings-keys';
import { XVII18nService } from '@app/i18n/services';

@Injectable()
export class TransferTotalFormService {
  /**
   * editor grid reference
   */
  public listGrid: XuxEditGridComponent;
  public listGridReady$: Subject<void> = new Subject();
  public totalsSubTitle$: BehaviorSubject<string> = new BehaviorSubject<string>(
    this.i18nService.translate(I18n.Forms.GridMessages.ShowingEntities, {
      count: 0,
      entities: this.i18nService.translate(I18n.Forms.GridMessages.Items),
    }),
  );

  public constructor(
    public numberFormatService: NumberFormatService,
    public i18nService: XVII18nService,
  ) {}

  /**
   * handle when list grid is ready
   *
   * @param action xux grid action
   * @param grid edit grid object
   */
  public handleListGridReady(
    action: XuxGridAction,
    grid: XuxEditGridComponent,
  ): void {
    this.listGrid = grid;
    this.listGridReady$.next();
    this.listGrid.xuxGridComponent.gridApi.setSortModel([
      { colId: 'name', sort: SORT_ORDERS.ASC },
    ]);
  }

  /**
   * get all columns obs
   *
   * @param formFieldPath form field path mapping, will using this to build field path or define
   * @param isShownSubmittedColumnsObs is need shown submitted columns observable,
   * if true will build submitted columns
   * @returns all columns obs
   */
  public getTotalGridColumnsObs(
    formFieldPath: ITransferFormFieldPath,
    isShownSubmittedColumnsObs: Observable<boolean>,
  ): Observable<IXVIListColumn[]> {
    const commonColumns: IXVIListColumn[] = [
      {
        title: I18n.EntityNames.DepartmentCode,
        path: formFieldPath.departmentCodeTo,
        sortable: false,
        defaultView: true,
        cellClassRules: {
          [COLUMN_ALIGN_RIGHT.cellClass]: ({ node }): boolean =>
            node.rowPinned === 'bottom',
        },
        cellRenderer: CELL_RENDERER.ag_group_cell_renderer,
        cellRendererParams: {
          suppressCount: true,
        },
        showRowGroup: true,
      },
      {
        title: I18n.Forms.GridForm.GLAccount,
        path: 'name',
        sortable: true,
        defaultView: true,
        filterParams: {
          quickFilter: true,
        },
        comparator: (valueA, valueB): number =>
          alphanumericComparator(valueA, valueB),
      },
    ];

    const initialQuantity: IXVIListColumn = {
      title: I18n.Forms.TransferForm.QtyRequested,
      path: formFieldPath.initialCount,
      sortable: true,
      defaultView: true,
      valueFormatter: (params: ValueFormatterParams): string =>
        !isNil(params.value)
          ? this.numberFormatService.getNumberFormatter(false)(params)
          : null,
      comparator: (valueA, valueB): number =>
        alphanumericComparator(valueA, valueB),
      ...COLUMN_ALIGN_RIGHT,
    };

    const totalCol: IXVIListColumn = {
      title: I18n.Forms.TransferForm.RequestCost,
      path: formFieldPath.totalCost,
      sortable: true,
      defaultView: true,
      valueFormatter: (params: ValueFormatterParams): string =>
        !isNil(params.value)
          ? this.numberFormatService.getNumberFormatter(true)(params)
          : null,
      comparator: (valueA, valueB): number =>
        alphanumericComparator(valueA, valueB),
      ...COLUMN_ALIGN_RIGHT,
    };

    const quantityCol: IXVIListColumn = {
      title: I18n.Forms.TransferForm.QtyReceived,
      path: formFieldPath.count,
      sortable: true,
      defaultView: true,
      hide: true,
      valueFormatter: (params: ValueFormatterParams): string =>
        !isNil(params.value)
          ? this.numberFormatService.getNumberFormatter(false)(params)
          : null,
      comparator: (valueA, valueB): number =>
        alphanumericComparator(valueA, valueB),
      ...COLUMN_ALIGN_RIGHT,
    };

    const receivedTotalCol: IXVIListColumn = {
      title: I18n.Forms.TransferForm.ReceivedCost,
      path: formFieldPath.receivedTotalCost,
      sortable: true,
      defaultView: true,
      hide: true,
      valueFormatter: (params: ValueFormatterParams): string =>
        !isNil(params.value)
          ? this.numberFormatService.getNumberFormatter(true)(params)
          : null,
      comparator: (valueA, valueB): number =>
        alphanumericComparator(valueA, valueB),
      ...COLUMN_ALIGN_RIGHT,
    };

    const quantityReversed: IXVIListColumn = {
      title: I18n.Forms.TransferForm.QtyReversed,
      path: formFieldPath.quantityReversed,
      defaultView: true,
      sortable: true,
      valueFormatter: (params: ValueFormatterParams): string =>
        !isNil(params.value)
          ? this.numberFormatService.getNumberFormatter(false)(params)
          : null,
      ...COLUMN_ALIGN_RIGHT,
    };

    const reversedCost: IXVIListColumn = {
      title: I18n.Forms.TransferForm.ReversedCost,
      path: formFieldPath.reversedCost,
      defaultView: true,
      sortable: true,
      valueFormatter: (params: ValueFormatterParams): string =>
        !isNil(params.value)
          ? this.numberFormatService.getNumberFormatter(true)(params)
          : null,
      ...COLUMN_ALIGN_RIGHT,
    };

    return isShownSubmittedColumnsObs.pipe(
      rx.map((isShownSubmittedColumns) =>
        concat(
          commonColumns,
          initialQuantity,
          totalCol,
          quantityReversed,
          reversedCost,
          {
            ...quantityCol,
            hide: !isShownSubmittedColumns,
          },
          {
            ...receivedTotalCol,
            hide: !isShownSubmittedColumns,
          },
        ),
      ),
    );
  }

  /**
   * get all columns obs
   *
   * @param formFieldPath form field path mapping, will using this to build field path or define
   * @param isAddAdditionalColumnsObs is need add additional columns observable, if true will combine additional columns
   */
  public getTotalsFormRules(
    formFieldPath: ITransferFormFieldPath,
    isAddAdditionalColumnsObs: Observable<boolean>,
  ): IFormRules {
    const totalFormFields: FieldList = {
      [formFieldPath.virtualTotals]: {
        widget: WIDGET_COMPONENT.edit_grid_custom,
        title: I18n.Common.Totals,
        descriptionObs: this.totalsSubTitle$,
        widgetData: {
          useVisibleItemCountDescription: false,
          showSearch: true,
          multiselect: false,
          orderControls: false,
          disableColumnSelector: true,
          treeViewMode: true,
          treeChildrenPropertyName: 'children',
          defaultExpanded: true,
          columnsObs: this.getTotalGridColumnsObs(
            formFieldPath,
            isAddAdditionalColumnsObs,
          ),
          userGridOptions: {
            treeColumnPath: 'children',
            deltaRowDataMode: false,
            rowClassRules: this.getFormRowClassRules(),
          },
          eventHandlers: {
            [XUX_GRID_ACTIONS.GRID_READY]: this.handleListGridReady.bind(this),
          },
          artificialInputCheckForSearchPanel: true,
        },
      },
    };

    return {
      fields: totalFormFields,
    };
  }

  /**
   * get list totals data handler subscription
   *
   * @param formService
   * @param callback Data Update Callback
   * @returns list totals data handler subscription
   */
  public handleTotalsSubscription(
    formService: IBaseFormService<ENTITY_TYPES.TRANSFER>,
    callback?: IGridInventoryItemVenueTotalFormCallBack,
  ): Subscription {
    const { form, formFieldPath } = formService;
    const { status, transfer_type } = form?.value;
    const noSkipEmptyValue = true;

    return observableCombineLatest([
      getFormControl(form, formFieldPath.inventoryItemVenues).valueChanges,
      this.listGridReady$.pipe(rx.first()),
    ])
      .pipe(
        rx.distinctUntilChanged(),
        rx.map(([items]) =>
          flatTreeDataByHasChildren<IGridFlatInventoryItemVenue>(items),
        ),
        rx.map((items) =>
          this.mapItemDataToTotalsData(
            items,
            formFieldPath,
            {
              status,
              transfer_type,
              site_department_code_to: getFormFieldValue(
                form,
                formFieldPath.departmentCodeTo,
              ),
            },
            noSkipEmptyValue,
          ),
        ),
        rx.tap((item) => {
          if (isFunction(callback)) callback(item);
        }),
      )
      .subscribe(this.updateListGridData.bind(this));
  }

  /**
   * filter item to totals data
   *
   * @param item inventory item
   * @param formFieldPath form field path mapping
   * @returns valid input data
   */
  public filterInputRequestedOrReceived(
    item: IGridFlatInventoryItemVenue,
    formFieldPath: ITransferFormFieldPath,
    formValue: Pick<ITransferData, 'transfer_type' | 'status'>,
  ): boolean {
    const { status, transfer_type } = formValue;
    const initialCount = item[formFieldPath.initialCount] || '0';
    const count = item[formFieldPath.count] || '0';

    return (
      item &&
      ((!isNil(initialCount) && !isEqual(initialCount, '0')) ||
        (getIsShowReceivedColStatus(transfer_type, status) &&
          !isNil(count) &&
          !isEqual(count, '0')))
    );
  }

  /**
   * convert items to totals data
   *
   * @param items inventory items
   * @param formFieldPath form field path mapping
   * @param formValue
   * @param noSkipEmptyValue
   * @param includeDepartmentCodeRow
   * @returns totals data
   */
  // eslint-disable-next-line max-params
  public mapItemDataToTotalsData(
    items: IGridFlatInventoryItemVenue[],
    formFieldPath: ITransferFormFieldPath,
    formValue: Pick<
      ITransferData,
      'transfer_type' | 'status' | 'site_department_code_to'
    >,
    noSkipEmptyValue: boolean = false,
    calcTotalPath?: string,
    includeDepartmentCodeRow: boolean = true,
  ): IGridInventoryItemVenueTotalFormData {
    const normalRows = this.buildNormalRows(
      items,
      formFieldPath,
      formValue,
      noSkipEmptyValue,
      calcTotalPath,
    );
    const itemsCount =
      head(normalRows).children.length + last(normalRows).children.length;

    if (includeDepartmentCodeRow) {
      const departmentCodeRow = this.buildDepartmentCodeRow(
        formValue,
        formFieldPath,
      );
      normalRows.unshift(departmentCodeRow);
    }
    const pinnedBottomData = this.buildPinnedData(
      normalRows,
      formFieldPath,
      includeDepartmentCodeRow,
    );

    return {
      normalRows,
      pinnedBottomData,
      itemsCount,
    };
  }

  /**
   * build pinned row Data
   *
   * @param formValue target form value
   * @param formFieldPath form field path mapping
   * @returns pinned row data, if baseOnRows empty will return null
   */
  public buildDepartmentCodeRow(
    formValue: Pick<ITransferData, 'site_department_code_to'>,
    formFieldPath: ITransferFormFieldPath,
  ): IGridFlatInventoryItemVenueTotal {
    const { site_department_code_to } = formValue;
    const departmentCodeRowData: IGridFlatInventoryItemVenueTotal = {
      site_department_code_to: this.i18nService.instant(
        DEPARTMENT_I18N_CODE_MAP[site_department_code_to],
      ),
      name: '',
      count: null,
      [formFieldPath.initialCount]: null,
      [formFieldPath.totalCost]: null,
    };

    return departmentCodeRowData;
  }

  /**
   * build normal rows
   *
   * @param items base inventory items
   * @param formFieldPath form field path mapping
   * @param formValue
   * @returns normal rows data, if items is empty, will return null
   */
  // eslint-disable-next-line max-params
  public buildNormalRows(
    items: IGridFlatInventoryItemVenue[],
    formFieldPath: ITransferFormFieldPath,
    formValue: Pick<ITransferData, 'transfer_type' | 'status'>,
    noSkipEmptyValue: boolean = false,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    calcTotalPath?: string,
  ): IGridFlatInventoryItemVenueTotal[] {
    const filteredItems = filter(items, (item) =>
      this.filterInputRequestedOrReceived(item, formFieldPath, formValue),
    );
    const treeData: IGridFlatInventoryItemVenueTotal[] = [
      {
        site_department_code_to: this.i18nService.instant(I18n.Common.Normal),
        categoryType: TOTALS_CATEGORY.normal,
        name: '',
        count: null,
        children: [],
      },
      {
        site_department_code_to: this.i18nService.instant(
          I18n.Common.Reversals,
        ),
        categoryType: TOTALS_CATEGORY.reversals,
        name: '',
        count: null,
        children: [],
      },
    ];
    const [normalData, reversalsData] = partition(filteredItems, (item) => {
      const initialCount = new DecimalNumber(item.initial_count);

      return initialCount.gte(0) || initialCount.isNaN;
    });
    const normalGroupedData = this.groupAndCalcInventoryItems(
      normalData,
      formFieldPath,
      formValue,
      noSkipEmptyValue,
      false,
    );
    const reversalsGroupedData = this.groupAndCalcInventoryItems(
      reversalsData,
      formFieldPath,
      formValue,
      noSkipEmptyValue,
      true,
    );
    set(treeData, '0.children', normalGroupedData);
    set(treeData, '1.children', reversalsGroupedData);

    return treeData;
  }

  /**
   * build pinned row Data
   *
   * @param baseOnRows pinned row will combine all base on rows
   * @param formFieldPath form field path mapping
   * @param includeDepartmentCodeRow flag to include extra field
   * @returns pinned row data, if baseOnRows empty will return null
   */
  public buildPinnedData(
    baseOnRows: IGridFlatInventoryItemVenueTotal[],
    formFieldPath: ITransferFormFieldPath,
    includeDepartmentCodeRow: boolean = true,
  ): [IGridFlatInventoryItemVenueTotal] {
    if (isEmpty(baseOnRows)) {
      // all rows empty, no pinned data
      return null;
    }
    let combineItems = [];
    // If included department code, baseOnRows length is 3, otherwise length is 2
    if (includeDepartmentCodeRow && baseOnRows.length === 3) {
      combineItems = [...baseOnRows[1].children, ...baseOnRows[2].children];
    } else if (baseOnRows.length === 2) {
      combineItems = [...baseOnRows[0].children, ...baseOnRows[1].children];
    }
    const totalCount = this.getSubTotal(combineItems, formFieldPath.count);
    const receivedTotalCost = this.getSubTotal(
      combineItems,
      formFieldPath.receivedTotalCost,
    );
    const initialCount = this.getSubTotal(
      combineItems,
      formFieldPath.initialCount,
    );
    const totalCost = this.getSubTotal(combineItems, formFieldPath.totalCost);
    const quantityReversed = this.getSubTotal(
      combineItems,
      formFieldPath.quantityReversed,
      true,
    );
    const reversedCost = this.getSubTotal(
      combineItems,
      formFieldPath.reversedCost,
      true,
    );

    const pinnedBottomData: IGridFlatInventoryItemVenueTotal = {
      name: '',
      category: this.i18nService.instant(I18n.Common.Totals),
      [formFieldPath.initialCount]: initialCount,
      total_cost: totalCost,
      [formFieldPath.quantityReversed]: quantityReversed,
      [formFieldPath.reversedCost]: reversedCost,
      count: totalCount,
      [formFieldPath.receivedTotalCost]: receivedTotalCost,
    };

    if (includeDepartmentCodeRow) {
      pinnedBottomData.category = '';
      pinnedBottomData.site_department_code_to = this.i18nService.instant(
        I18n.Common.Totals,
      );
    }

    return [pinnedBottomData];
  }

  /**
   * get sub total of data
   *
   * @param items inventory item or inventory total item
   * @param targetPath field path which need to calc sub total, if not exist or isNaN will use zero to replace it.
   * @returns items target field sub total
   */
  public getSubTotal(
    items: IGridFlatInventoryItemVenue[] | IGridFlatInventoryItemVenueTotal[],
    targetPath: string,
    noSkipEmptyValue: boolean = false,
  ): DecimalNumber {
    if (!noSkipEmptyValue) {
      const hasInput = some(items, (item) => !!item?.[targetPath]);
      if (!hasInput) return null;
    }

    return reduce(
      items,
      (pre, cur) => {
        const curNumber = new DecimalNumber(cur?.[targetPath]);

        return pre.add(curNumber.isNaN ? 0 : curNumber);
      },
      new DecimalNumber(0),
    );
  }

  public getTotalCost(
    itemList,
    formFieldPath: ITransferFormFieldPath,
    formValue: Pick<ITransferData, 'transfer_type' | 'status'>,
    calcTotalPath?: string,
  ): DecimalNumber {
    const { status, transfer_type } = formValue;
    const isFinalStatus = includes(TRANSFER_FINAL_STATUS, status);

    return reduce(
      itemList,
      (pre, curItem) => {
        let quantityPath;
        if (!isNil(calcTotalPath)) {
          quantityPath = calcTotalPath;
        } else {
          quantityPath = getIsShowReceivedColStatus(transfer_type, status)
            ? formFieldPath.count
            : formFieldPath.initialCount;
        }
        const quantityValue = new DecimalNumber(curItem?.[quantityPath] || 0);
        const receivedUnitCost = isFinalStatus
          ? new DecimalNumber(curItem?.[formFieldPath.itemCost] || 0)
          : calculateUnitCost({ data: { ...curItem } });

        return pre.add(quantityValue.mul(receivedUnitCost));
      },
      new DecimalNumber(0),
    );
  }

  /**
   * get from row class rules
   * data-pinned-bottom: for bottom pinned row style
   *
   * @returns style build model
   */
  public getFormRowClassRules(): IGridRowOrCellClassRule {
    return {
      'data-pinned-bottom': (params: CellClassParams): boolean =>
        params.node.isRowPinned(),
    };
  }

  /**
   * update list grid data by totalsData
   *
   * @param totalsData totalsData
   */
  public updateListGridData(
    totalsData: IGridInventoryItemVenueTotalFormData,
  ): void | null {
    if (isNil(totalsData)) return null;
    this.listGrid?.forceUpdateXuxGridItems(totalsData.normalRows);
    this.listGrid?.xuxGridComponent?.gridApi?.setPinnedBottomRowData(
      totalsData.pinnedBottomData,
    );

    const sitesSubTitle = this.i18nService.translate(
      I18n.Forms.GridMessages.ShowingEntities,
      {
        count: totalsData.itemsCount,
        entities: this.i18nService.translate(I18n.Forms.GridMessages.Items),
      },
    );

    this.totalsSubTitle$.next(sitesSubTitle);
  }

  // eslint-disable-next-line max-params
  private groupAndCalcInventoryItems(
    items: IGridFlatInventoryItemVenue[],
    formFieldPath: ITransferFormFieldPath,
    formValue: Pick<ITransferData, 'transfer_type' | 'status'>,
    noSkipEmptyValue: boolean = false,
    isReversal: boolean = false,
  ): IGridFlatInventoryItemVenueTotal[] {
    return chain(items)
      .groupBy(formFieldPath.glAccountName)
      .map((groupedItemList, glAccountName) => {
        if (isReversal) {
          return <IGridFlatInventoryItemVenueTotal>{
            site_department_code_to: '',
            name: glAccountName,
            count: this.getSubTotal(
              groupedItemList,
              formFieldPath.count,
              noSkipEmptyValue,
            ),
            received_total_cost: this.getTotalCost(
              groupedItemList,
              formFieldPath,
              formValue,
              formFieldPath.count,
            ),
            quantity_reversed: this.getSubTotal(
              groupedItemList,
              formFieldPath.initialCount,
              noSkipEmptyValue,
            ),
            reversed_cost: this.getTotalCost(
              groupedItemList,
              formFieldPath,
              formValue,
              formFieldPath.initialCount,
            ),
          };
        }

        return <IGridFlatInventoryItemVenueTotal>{
          site_department_code_to: '',
          name: glAccountName,
          count: this.getSubTotal(
            groupedItemList,
            formFieldPath.count,
            noSkipEmptyValue,
          ),
          received_total_cost: this.getTotalCost(
            groupedItemList,
            formFieldPath,
            formValue,
            formFieldPath.count,
          ),
          [formFieldPath.initialCount]: this.getSubTotal(
            groupedItemList,
            formFieldPath.initialCount,
            noSkipEmptyValue,
          ),
          total_cost: this.getTotalCost(
            groupedItemList,
            formFieldPath,
            formValue,
            formFieldPath.initialCount,
          ),
        };
      })
      .filter((item) => {
        return (
          isNumber(item.count) ||
          isNumber(item[formFieldPath.initialCount]) ||
          isNumber(item[formFieldPath.quantityReversed])
        );
      })
      .value();
  }
}
